import React, { useEffect, useState } from 'react';

import { useAppSelector } from '../hooks/redux';
import { selectUser } from '../store/auth.slice';
import { ContentCopy } from '../components/icons/content-copy';
import { MuiRefreshIcon } from '../components/icons/mui-refresh';
import { useCreateReferalCodeMutation, useGetReferalCodesQuery, useGetReferalUsersQuery } from '../services/users.api';
import { maskEmail } from '../utils/maskEmail';
import { useGetPartnerReferralsQuery } from '../services/partners.api';

type TabKey = 'psychologists' | 'payments';

const ReferalManagement = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('psychologists');
  const [referalCode, setReferalCode] = useState('');

  const {
    data: partnersPayment,
    isLoading: isPaymentLoading,
    refetch: refetchPayments,
    isFetching: isFetchingPayments,
  } = useGetPartnerReferralsQuery();
  const { data: referredUsers, refetch, isFetching, isLoading, error } = useGetReferalUsersQuery();
  const { data: referalCodes } = useGetReferalCodesQuery();
  const [createReferal] = useCreateReferalCodeMutation();

  const userData = useAppSelector(selectUser);

  const tabs: { key: TabKey; label: string }[] = [{ key: 'psychologists', label: 'Психологи' }];

  if (partnersPayment?.data?.length) {
    tabs.push({ key: 'payments', label: 'История начислений' });
  }

  const copyToClipboard = () => {
    const name = `${userData?.last_name || ''} ${userData?.first_name || ''} ${userData?.middle_name || ''}`.trim();
    const message = `Вас приглашает ${name} зарегистрироваться в системе ZiNGiZi.\nПерейдите по ссылке: https://lk.zingizi.pro/registration\nВведите Пригласительный ключ: ${referalCode}`;
    navigator.clipboard.writeText(message);
  };

  useEffect(() => {
    if (isFetching || isLoading) return;
    if (!referalCodes || referalCodes.codes.length === 0) {
      createReferal();
    } else {
      setReferalCode(referalCodes.codes.at(-1) || '');
    }
  }, [createReferal, referalCodes, isFetching, isLoading]);

  return (
    <div className="flex flex-col gap-[30px] w-full pb-[85px]">
      <p className="text-title">
        <strong>Приглашайте коллег-психологов в ZiNGiZi и получайте бонусы!</strong>
        <br />
        <br />
        За каждую регистрацию по вашему <strong>Пригласительному ключу</strong> вы получаете{' '}
        <strong>100 бонусных рублей</strong>. <br />
        Каждому зарегистрированному по вашему ключу психологу также будет начислено <strong>100 бонусных рублей</strong>
        .
        <br />
        <br />
        Курс: 1 бонусный рубль = 1 рубль. <br />
        <strong>Бонусными рублями</strong> можно оплатить часть стоимости подписки, обучающих курсов и других услуг на
        ZiNGiZi.
      </p>

      <div className="flex flex-wrap gap-[10px] mb-6">
        <span className="flex items-center">Ваш ключ:</span>
        <div className="h-[40px] lg:h-[48px] relative !h-full w-[160px]">
          <input
            type="text"
            value={referalCode}
            readOnly
            className="w-full h-full lg:h-[48px] p-[10px_15px] md:p-[13px_20px] border border-greyBase focus:border-2 rounded-[10px] text-blackBase !pr-[44px] font-mono text-sm"
          />
          <span className="absolute top-[50%] right-[6px] -translate-y-1/2">
            <button
              onClick={copyToClipboard}
              className="w-full flex justify-center items-center gap-2.5 !p-[5px_8px] !text-sm !font-medium !rounded-[10px] text-white bg-redBase border-redBase hover:border-redBaseWithBlur20 hover:bg-redBaseWithBlur20 active:border-blackBase active:bg-redBase"
            >
              <ContentCopy className="w-[18px] h-[18px]" fill="#FFFFFF" />
            </button>
          </span>
        </div>
      </div>

      <p className="text-subtitle">Психологи открывшие ZiNGiZi Вашим ключом:</p>

      <div className="flex border-b border-gray-200 font-comfortaa mb-4">
        {tabs.map((t) => {
          const active = activeTab === t.key;

          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as TabKey)}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors
                ${active ? 'text-redBase border-redBase' : 'text-greyBase border-transparent hover:border-grey3'}
              `}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'payments' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-greyBase">
            <thead>
              <tr className="bg-grey6 text-greyDark uppercase text-xs font-semibold tracking-wide">
                <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Email</th>
                <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Имя</th>
                <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Название оплаченного тарифа</th>
                <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Вознаграждение (10%)</th>
                <th className="py-[10px] px-[20px] border-b border-grey3 text-left">
                  <div className="flex items-center justify-between gap-1">
                    Дата оплаты
                    <button onClick={refetchPayments} className="text-greyBase hover:text-blackBase transition-colors">
                      <MuiRefreshIcon className={`w-[20px] h-[20px] ${isFetchingPayments ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {partnersPayment?.data?.map((partner, idx) => (
                <tr key={idx}>
                  <td className="py-[10px] px-[20px] border-b border-grey3 text-ellipsis whitespace-nowrap">
                    {maskEmail(partner.email_masked)}
                  </td>
                  <td className="py-[10px] px-[20px] border-b border-grey3">{partner.full_name}</td>
                  <td className="py-[10px] px-[20px] border-b border-grey3 text-ellipsis whitespace-nowrap">
                    {partner.plan_name}
                  </td>
                  <td className="py-[10px] px-[20px] border-b border-grey3 text-ellipsis whitespace-nowrap">
                    {partner.reward}
                  </td>
                  <td className="py-[10px] px-[20px] border-b border-grey3">
                    {new Date(partner.created_at).toLocaleDateString('ru-RU')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'psychologists' && (
        <>
          {referredUsers?.users.length === 0 ? (
            <div className="text-sm text-greyBase">Пока никто не зарегистрировался по вашему ключу.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-greyBase">
                <thead>
                  <tr className="bg-grey6 text-greyDark uppercase text-xs font-semibold tracking-wide">
                    <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Email</th>
                    <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Имя и Фамилия</th>
                    <th className="py-[10px] px-[20px] border-b border-grey3 text-left">Дата регистрации</th>
                    <th className="py-[10px] px-[20px] border-b border-grey3 text-left">
                      <div className="flex items-center justify-between gap-1">
                        Исп. ключ
                        <button onClick={refetch} className="text-greyBase hover:text-blackBase transition-colors">
                          <MuiRefreshIcon className={`w-[20px] h-[20px] ${isFetching ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {referredUsers?.users.map((user, idx) => (
                    <tr key={idx}>
                      <td className="py-[10px] px-[20px] border-b border-grey3 text-ellipsis whitespace-nowrap">
                        {maskEmail(user.email)}
                      </td>
                      <td className="py-[10px] px-[20px] border-b border-grey3">{`${user.firstName} ${user.lastNameMasked}`}</td>
                      <td className="py-[10px] px-[20px] border-b border-grey3">
                        {new Date(user.registrationDate).toLocaleDateString('ru-RU')}
                      </td>
                      <td className="py-[10px] px-[20px] border-b border-grey3 text-ellipsis whitespace-nowrap">
                        {user.usedReferalCode}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReferalManagement;
